import AdFraudDetection from './identifiers/ad_fraud_detection';
import Identifier from './identifiers/identifiers';
import { CookieUtils } from './types/cookies_utils';
import { MetadataUtils } from './types/metadata_utils';
import Visitor from './visitor';

class Refcodes {
  constructor(
    private readonly cookieUtils: CookieUtils,
    private readonly metadataUtils: MetadataUtils,
  ) {}

  // SEO if referral from google, bing, yahoo, aol, ask = catches ~99.8% of all organic visits
  SEO_DOMAINS = [
    'www.google.',
    'www.bing.com',
    'www.ask.com',
    'search.yahoo.',
    'search.aol.',
    'duckduckgo.com',
  ];

  // Returns object of cookie values if sendToAnalytics = true
  // Else return {}
  processReferralInformation() {
    const referrerDetails = this.getReferralInformation();
    let sendToAnalytics = true;
    let debugRefcodes = false;

    // Capybara/poltergeist doesn't like this!!!!??? WHY!?!?!
    if (
      referrerDetails.referringDomain.indexOf('gusto') >= 0 ||
      referrerDetails.referringDomain.indexOf('zenpayroll') >= 0
    ) {
      // TODO: remove this once we figure out why source and landing_page are null for 10% of leads
      // return {};
      debugRefcodes = true;
    }

    const identifier = new Identifier(this.cookieUtils);
    new AdFraudDetection(identifier, this.cookieUtils).getAdFraudDetectionId();
    const visitorId = new Visitor(identifier, this.cookieUtils).getVisitorId();

    // Check if source already exists. If not, collect all possible data
    // If found, do nothing
    if (!this.cookieUtils.getCookie('so')) {
      // Set cookie values here
      this.cookieUtils.setCookie('so', referrerDetails.source);
      this.cookieUtils.setCookie('lp', referrerDetails.landingPage, undefined, 4096);
      this.cookieUtils.setCookie('ca', referrerDetails.campaign);
      this.cookieUtils.setCookie('co', referrerDetails.content);
      this.cookieUtils.setCookie('mt', referrerDetails.matchType);
      this.cookieUtils.setCookie('kw', referrerDetails.keyword);
      this.cookieUtils.setCookie('cr', referrerDetails.creative);
      this.cookieUtils.setCookie('gc', referrerDetails.gclid);
      this.cookieUtils.setCookie('pl', referrerDetails.placement);
      this.cookieUtils.setCookie('dv', referrerDetails.device);
      this.cookieUtils.setCookie('ne', referrerDetails.network);
      this.cookieUtils.setCookie('me', referrerDetails.medium);
      this.cookieUtils.setCookie('ad', referrerDetails.adgroup);
      this.cookieUtils.setCookie('ms', referrerDetails.msclkid);
      this.cookieUtils.setCookie('ru', referrerDetails.referringUrl, undefined, 4096);
    }
    // TODO: remove this once we figure out why source and landing_page are null for 10% of leads
    // All cookie values are for debugging purposes so we shouldn't compare it to old values
    // Setting sendToAnalytics = false and bypass comparison process. This will allow us to
    // send current values as debug_ properties
    else {
      sendToAnalytics = false;
    }

    // Send to snowplow, only on first visit
    if (sendToAnalytics && !debugRefcodes) {
      return {
        visitor_id: visitorId,
        source: referrerDetails.source,
        landing_page: referrerDetails.landingPage,
        campaign: referrerDetails.campaign,
        content: referrerDetails.content,
        match_type: referrerDetails.matchType,
        keyword: referrerDetails.keyword,
        creative: referrerDetails.creative,
        gclid: referrerDetails.gclid,
        placement: referrerDetails.placement,
        device: referrerDetails.device,
        network: referrerDetails.network,
        medium: referrerDetails.medium,
        adgroup: referrerDetails.adgroup,
        msclkid: referrerDetails.msclkid,
        referring_url: referrerDetails.referringUrl,
      };
    }
    // TODO: remove this once we figure out why source and landing_page are null for 10% of leads
    return {
      debug_source: this.cookieUtils.getCookie('so') || referrerDetails.source,
      debug_landing_page: this.cookieUtils.getCookie('lp') || referrerDetails.landingPage,
      debug_campaign: this.cookieUtils.getCookie('ca') || referrerDetails.campaign,
      debug_content: this.cookieUtils.getCookie('co') || referrerDetails.content,
      debug_match_type: this.cookieUtils.getCookie('mt') || referrerDetails.matchType,
      debug_keyword: this.cookieUtils.getCookie('kw') || referrerDetails.keyword,
      debug_creative: this.cookieUtils.getCookie('cr') || referrerDetails.creative,
      debug_gclid: this.cookieUtils.getCookie('gc') || referrerDetails.gclid,
      debug_placement: this.cookieUtils.getCookie('pl') || referrerDetails.placement,
      debug_device: this.cookieUtils.getCookie('dv') || referrerDetails.device,
      debug_network: this.cookieUtils.getCookie('ne') || referrerDetails.network,
      debug_medium: this.cookieUtils.getCookie('me') || referrerDetails.medium,
      debug_adgroup: this.cookieUtils.getCookie('ad') || referrerDetails.adgroup,
      debug_msclkid: this.cookieUtils.getCookie('ms') || referrerDetails.msclkid,
      debug_referring_url: this.cookieUtils.getCookie('ru') || referrerDetails.referringUrl,
    };
  }

  // TODO: remove this once we figure out why source and landing_page are null for 10% of leads
  isDebugRefcodes(refcodes: any) {
    return Object.prototype.hasOwnProperty.call(refcodes, 'debug_source');
  }

  getReferringDomainFromReferrer(referringUrl: string) {
    return String(referringUrl.split('/')[2]);
  }

  // Function takes 1+ parameter names and returns the value of the first one that exists
  getFirstValueForParamNames(...args: string[]): string | undefined {
    const paramNames = args.slice();

    // Capybara/poltergeist/whatever test this barfs on [].find
    while (paramNames.length) {
      const paramName = paramNames.shift();
      let paramValue;
      if (paramName) {
        paramValue = this.getParameterByName(paramName);
      }
      if (paramValue) {
        return paramValue;
      }
    }

    return undefined;
  }

  // Function takes 1 argument: the parameter name you're looking for in the URL.
  // If found, it returns the value of the param.
  getParameterByName(paramName: string): string | false {
    const queryRegexString = `[\\?&]${paramName}=([^&#]*)`;
    const regex = new RegExp(queryRegexString, 'i');
    const results = regex.exec(this.metadataUtils.getWindowLocationSearch());

    if (results === null) {
      return false;
    }
    // TODO: Unclear if this is even necessary or doing anything
    this.metadataUtils.getWindowLocationSearch().replace(queryRegexString, '');

    return decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  getReferralInformation() {
    let source;
    let campaign: string | undefined = '';
    let content: string | undefined = '';
    let matchType: string | undefined = '';
    let keyword: string | undefined = '';
    let creative: string | undefined = '';
    let gclid: string | undefined = '';
    let placement: string | undefined = '';
    let device: string | undefined = '';
    let network: string | undefined = '';
    let medium: string | undefined = '';
    let adgroup: string | undefined = '';
    let msclkid: string | undefined = '';
    const sendToAnalytics = true;
    const debugRefcodes = false;

    // If navigating around our sites, don't run the referralCookies again
    const referringUrl = this.metadataUtils.getDocumentReferrer();
    const referringDomain = this.getReferringDomainFromReferrer(referringUrl);

    // Set Landing Page Cookie. Key => lp, Value => url of first page view
    const landingPage = this.metadataUtils.getWindowLocationHref();

    // If there is a utm_source in a URL, we can assume we put them there
    // and they are part of a campaign or link building effort we are running.
    // In those cases there will/should always be a utm_source at minimum.

    // Take utm_source before vt_source
    const sourceValue = this.getFirstValueForParamNames('utm_source', 'vt_source');

    if (sourceValue) {
      source = sourceValue;
      campaign = this.getFirstValueForParamNames('utm_campaign', 'vt_campaign'); // campaign
      content = this.getFirstValueForParamNames('utm_content', 'vt_content'); // content
      matchType = this.getFirstValueForParamNames('matchtype'); // matchtype
      keyword = this.getFirstValueForParamNames('keyword'); // keyword
      creative = this.getFirstValueForParamNames('creative'); // creative
      gclid = this.getFirstValueForParamNames('gclid'); // google ad click ID
      placement = this.getFirstValueForParamNames('placement'); // google display ad url
      device = this.getFirstValueForParamNames('device'); // device
      network = this.getFirstValueForParamNames('network');
      medium = this.getFirstValueForParamNames('medium');
      adgroup = this.getFirstValueForParamNames('adgroup');
      msclkid = this.getFirstValueForParamNames('msclkid');
    }
    // If no params in URL, but we have a referral URL, it's either SEO or Inbound/Referral
    else if (referringUrl) {
      if (this.SEO_DOMAINS.find(domain => referringUrl.indexOf(domain) > -1)) {
        // If in the seoDomains array, it's SEO
        // Set source => SEO
        // inArray function returns -1 if not found in array. Thus > -1 = something matched
        source = 'SEO';
      }
      // If referral and not search engine, it's inbound
      // Set source => Inbound Link && network => referring url
      else {
        source = 'Inbound Referral';
      }
    }
    // If no utm_source && no referring URL, it's a Direct visit
    // Set source => Direct
    else {
      source = 'Direct';
    }

    return {
      source,
      campaign,
      content,
      matchType,
      keyword,
      creative,
      gclid,
      placement,
      device,
      network,
      medium,
      adgroup,
      msclkid,
      sendToAnalytics,
      debugRefcodes,
      referringUrl,
      referringDomain,
      landingPage,
    };
  }
}

export default Refcodes;
